import { useEffect, useMemo, useState } from 'react';
import { useAtom } from 'jotai';
import type {
  PreDrillWorkspace,
  WellborePointsResponse,
} from 'types/api-responses';
import {
  intersectedGridsPath,
  wellborePointsPath,
} from 'types/wellbore-geometry-workspace';
import {
  useIntersectedGridsAtom,
  useWellboreDetailsAtom,
  useWellborePointsAtom,
} from './atoms';

interface UseGeometrySheetReadsArgs {
  workspace: PreDrillWorkspace;
  /** The Well Workspace hosting the sheet; without it none of these reads are made. */
  wellWorkspaceId?: number | null;
}

/**
 * The geometry tearsheet's reads (points, intersected grids, details) and what the
 * sheet derives from the points. Every read is gated on its href, since a backend
 * without the feature never resolves them.
 * @param {UseGeometrySheetReadsArgs} args The workspace and its host.
 * @return {object} The reads, their refetches and the derived flags.
 */
export const useGeometrySheetReads = ({
  workspace,
  wellWorkspaceId,
}: UseGeometrySheetReadsArgs) => {
  const pointsHref = wellWorkspaceId
    ? wellborePointsPath(wellWorkspaceId, workspace.uwi14)
    : undefined;
  const [pointsResource, refetchPoints] = useAtom(
    useWellborePointsAtom(pointsHref)
  );
  // A write's answer is held until the re-read it triggers lands, so the map and the
  // table don't fall back to the pre-write set (a saved drag snapping back).
  const [answeredPoints, setAnsweredPoints] =
    useState<WellborePointsResponse | null>(null);
  useEffect(() => {
    setAnsweredPoints(null);
  }, [pointsResource.data]);
  const pointsData =
    pointsHref && pointsResource.data && !('error' in pointsResource.data)
      ? (answeredPoints ?? pointsResource.data)
      : null;
  // What the map needs to know before it may call a bore a two-point stick: the points read
  // has landed, or failed (review summary: a failed read said "Simple Stick" instead of an error).
  // A backend with no points endpoint has nothing to wait for.
  const pointsFailed =
    !!pointsHref && !!pointsResource.data && 'error' in pointsResource.data;
  const pointsLoaded =
    pointsData !== null || (!pointsResource.loading && !pointsFailed);
  const mapPointsState = useMemo(
    () => ({ pointsLoaded, pointsFailed }),
    [pointsLoaded, pointsFailed]
  );

  // Story 8: the intersected grids (TVDSS ranges + permit placement), same gating.
  const gridsHref = wellWorkspaceId
    ? intersectedGridsPath(wellWorkspaceId, workspace.uwi14)
    : undefined;
  const [gridsResource, refetchGrids] = useAtom(
    useIntersectedGridsAtom(gridsHref)
  );
  const intersectedGrids =
    gridsHref && gridsResource.data && !('error' in gridsResource.data)
      ? gridsResource.data
      : null;
  // The details card's read, held here only where the datum can be edited: a saved
  // datum changes the elevations the card shows, so the card re-reads on it.
  const detailsAtom = useWellboreDetailsAtom(
    pointsHref ? workspace.resources.wellboreDetails.href : undefined
  );
  const [, refetchDetails] = useAtom(detailsAtom);

  // What the backend says may be done with this bore's geometry.
  const geometryEditable = pointsData?.editable ?? false;
  const canEditGeometry =
    geometryEditable &&
    (pointsData?.resources.geometryEdits.auth.includes('create') ?? false);
  const canRefreshGeometry =
    geometryEditable &&
    (pointsData?.resources.geometryRefreshes.auth.includes('create') ?? false);
  // A geometry already failed on open has no push coming; only the payload says so.
  const geometryFailedOnRead = pointsData?.geometryStatus === 'failed';
  // Story 8: a survey arrives as survey points, so a bore with none has no survey on
  // file. Null points is no answer, so this stays false outside the tearsheet.
  const noDirectionalSurvey =
    pointsData !== null &&
    !pointsData.points.some(
      (point) =>
        point.source === 'survey_station' || point.source === 'imported_survey'
    );
  // UAT 25: every point is the filed survey, untouched, which the backend keeps unless
  // told to replace it; a station is untouched until it is given a depth of its own.
  const surveyUntouched =
    pointsData !== null &&
    pointsData.points.length > 0 &&
    pointsData.points.every(
      (point) =>
        point.source === 'survey_station' &&
        point.depthSource !== 'user' &&
        !point.depthLocked
    );
  // Whether the bore has any depth at all, which the depthless banner reads (UAT 37).
  const pointsHaveDepths = useMemo(
    () =>
      (pointsData?.points ?? []).some(
        (point) => point.tvdFt !== null || point.tvdssFt !== null
      ),
    [pointsData]
  );
  // A vendor survey behind the points — arming edit then warns about confidence.
  const surveyed = useMemo(
    () =>
      (pointsData?.points ?? []).some(
        (point) =>
          /survey/i.test(point.source) && !/synthetic|user/i.test(point.source)
      ),
    [pointsData]
  );

  return {
    pointsHref,
    pointsLoading: pointsResource.loading,
    mapPointsState,
    pointsData,
    refetchPoints,
    setAnsweredPoints,
    gridsHref,
    intersectedGrids,
    refetchGrids,
    detailsAtom,
    refetchDetails,
    geometryEditable,
    canEditGeometry,
    canRefreshGeometry,
    geometryFailedOnRead,
    noDirectionalSurvey,
    surveyUntouched,
    pointsHaveDepths,
    surveyed,
  };
};
